import styled from '@emotion/styled';
import { Autocomplete } from '@mui/material';
import * as enabledApi from '@shared/api/enabledPlayApi';
import { CardBorder } from '@shared/components/core/card/CommonCardStyles';
import { Switch } from '@shared/components/core/field/CommonFieldStyles';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { BodyMedium, BodySmall, TitleXSmall } from '@shared/components/core/text/Typography';
import { extractRoleProperties } from '@shared/hooks/helpers/OrganizationalHelpers';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import { OrganizationAgentExtensionPermissions } from '@shared/models/organizations/OrganizationMemberRole';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IExtensionPermissionsManagerProps {
    organization: OrganizationModel;
}

/** The four org-wide allow toggles, keyed by their OrganizationModel field. */
const ORG_ALLOW_FLAGS: (keyof OrganizationModel)[] = [
    'allowPublicSkills',
    'allowMemberManagedSkills',
    'allowPublicMcpServers',
    'allowMemberManagedMcpServers',
];

/** The six per-member extension permissions, keyed by their model field. */
const MEMBER_EXTENSION_FLAGS: (keyof OrganizationAgentExtensionPermissions)[] = [
    'canShareSkillsWithOrganization',
    'canManageOrganizationSkills',
    'canUseExternalSkills',
    'canShareMcpServersWithOrganization',
    'canManageOrganizationMcpServers',
    'canUseExternalMcpServers',
];

export default function ExtensionPermissionsManager({ organization }: Readonly<IExtensionPermissionsManagerProps>) {
    const { t } = useTranslation();
    const [savingFlag, setSavingFlag] = useState<string | null>(null);

    const setOrgFlag = async (flag: keyof OrganizationModel, value: boolean) => {
        setSavingFlag(flag);
        try {
            // Send the required fields plus the toggled flag; nullable governance flags on the API mean
            // omitted flags are left unchanged, so we only need to include the one we're changing.
            const result = await enabledApi.updateOrganization(organization.id, {
                name: organization.name,
                description: organization.description,
                customResourcesMarkdown: organization.customResourcesMarkdown ?? '',
                customBillingUrl: organization.customBillingUrl ?? '',
                [flag]: value,
            });
            if (result.resultType !== 'Ok') throw new Error('Unable to update organization');
            await useOrganizationStore.getState().loadCurrentOrganizationDetails(organization.id);
            useAlertsStore.getState().showSuccess(t('common:organization.governance.extensions.permissions.saved'));
        } catch {
            useAlertsStore.getState().showError(t('common:organization.governance.extensions.permissions.saveError'));
        } finally {
            setSavingFlag(null);
        }
    };

    return (
        <Wrap>
            <SubSectionRow
                title={t('common:organization.governance.extensions.permissions.orgTitle')}
                variant="small"
            />
            <BodyMedium>{t('common:organization.governance.extensions.permissions.orgDescription')}</BodyMedium>
            <ToggleCard>
                {ORG_ALLOW_FLAGS.map((flag) => (
                    <ToggleRow key={flag}>
                        <div>
                            <TitleXSmall>
                                {t(`common:organization.governance.extensions.orgFlags.${flag}.title`)}
                            </TitleXSmall>
                            <BodySmall>
                                {t(`common:organization.governance.extensions.orgFlags.${flag}.description`)}
                            </BodySmall>
                        </div>
                        <Switch
                            checked={organization[flag] !== false}
                            disabled={savingFlag === flag}
                            onChange={(event) => setOrgFlag(flag, event.target.checked)}
                        />
                    </ToggleRow>
                ))}
            </ToggleCard>

            <MemberPermissions organizationId={organization.id} />
        </Wrap>
    );
}

/** Pick a member and toggle their individual skill/connector share/manage/use-external permissions. */
function MemberPermissions({ organizationId }: Readonly<{ organizationId: string }>) {
    const { t } = useTranslation();
    const [members, setMembers] = useState<OrganizationMemberModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState<OrganizationMemberModel | null>(null);
    const [savingFlag, setSavingFlag] = useState<string | null>(null);

    useEffect(() => {
        const loadMembers = async () => {
            setIsLoading(true);
            try {
                const result = await enabledApi.getOrganizationMembers(organizationId, { take: 200, skip: 0 });
                if (result.resultType !== 'Ok' || !result.data) throw new Error('Unable to load members');
                setMembers(result.data);
            } catch {
                useAlertsStore.getState().showError(t('common:organization.governance.extensions.members.loadError'));
            } finally {
                setIsLoading(false);
            }
        };
        loadMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const setMemberFlag = async (flag: keyof OrganizationAgentExtensionPermissions, value: boolean) => {
        if (!selected) return;
        setSavingFlag(flag);
        // Build a full request from the member's current role + extension perms so nothing is clobbered.
        const request = {
            ...extractRoleProperties(selected),
            canUseExternalSkills: selected.canUseExternalSkills ?? false,
            canUseExternalMcpServers: selected.canUseExternalMcpServers ?? false,
            canShareSkillsWithOrganization: selected.canShareSkillsWithOrganization ?? false,
            canShareMcpServersWithOrganization: selected.canShareMcpServersWithOrganization ?? false,
            canManageOrganizationSkills: selected.canManageOrganizationSkills ?? false,
            canManageOrganizationMcpServers: selected.canManageOrganizationMcpServers ?? false,
            [flag]: value,
        };
        try {
            const result = await enabledApi.updateOrganizationMember(selected.id, request);
            if (result.resultType !== 'Ok' || !result.data) throw new Error('Unable to update member');
            setSelected(result.data);
            setMembers((prev) => prev.map((m) => (m.id === result.data!.id ? result.data! : m)));
            useAlertsStore.getState().showSuccess(t('common:organization.governance.extensions.members.saved'));
        } catch {
            useAlertsStore.getState().showError(t('common:organization.governance.extensions.members.saveError'));
        } finally {
            setSavingFlag(null);
        }
    };

    const sortedMembers = useMemo(
        () => [...members].sort((a, b) => (a.user?.email ?? '').localeCompare(b.user?.email ?? '')),
        [members]
    );

    return (
        <>
            <SubSectionRow title={t('common:organization.governance.extensions.members.title')} variant="small" />
            <BodyMedium>{t('common:organization.governance.extensions.members.description')}</BodyMedium>
            {isLoading ? (
                <SpacedLoader />
            ) : (
                <>
                    <Autocomplete
                        options={sortedMembers}
                        value={selected}
                        onChange={(_, member) => setSelected(member)}
                        getOptionLabel={(member) => member.user?.email ?? member.userId}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                            <StyledTextField
                                {...params}
                                label={t('common:organization.governance.extensions.members.memberLabel')}
                            />
                        )}
                    />
                    {selected && (
                        <ToggleCard>
                            {selected.isAdmin && (
                                <BodySmall>
                                    {t('common:organization.governance.extensions.members.adminNote')}
                                </BodySmall>
                            )}
                            {MEMBER_EXTENSION_FLAGS.map((flag) => (
                                <ToggleRow key={flag}>
                                    <div>
                                        <TitleXSmall>
                                            {t(`common:organization.governance.extensions.memberFlags.${flag}.title`)}
                                        </TitleXSmall>
                                        <BodySmall>
                                            {t(
                                                `common:organization.governance.extensions.memberFlags.${flag}.description`
                                            )}
                                        </BodySmall>
                                    </div>
                                    <Switch
                                        checked={selected.isAdmin || (selected[flag] ?? false)}
                                        disabled={selected.isAdmin || savingFlag === flag}
                                        onChange={(event) => setMemberFlag(flag, event.target.checked)}
                                    />
                                </ToggleRow>
                            ))}
                        </ToggleCard>
                    )}
                </>
            )}
        </>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
`;

const ToggleCard = styled(CardBorder)`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
`;

const ToggleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;

    & > div:first-of-type {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
    }
`;
